import React, { useState, useEffect } from 'react';
import { User, Lock, Save } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useAuth } from '../../context/AuthContext';

export default function Profile() {
  const { logout } = useAuth();
  const [profile, setProfile] = useState({ name: '', email: '' });
  const [passwords, setPasswords] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [loading, setLoading] = useState(true);
  const [isSubmittingProfile, setIsSubmittingProfile] = useState(false);
  const [isSubmittingPassword, setIsSubmittingPassword] = useState(false);

  useEffect(() => {
    api.get('/auth/me')
      .then(r => setProfile({ name: r.data.name || '', email: r.data.email || '' }))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    if (!profile.email.trim()) { toast.error('E-posta gerekli'); return; }
    setIsSubmittingProfile(true);
    try {
      const res = await api.put('/auth/profile', profile);
      toast.success('Profil güncellendi');
      // If email changed, update token
      if (res.data.token) {
        localStorage.setItem('admin_token', res.data.token);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata oluştu');
    } finally {
      setIsSubmittingProfile(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!passwords.current_password || !passwords.new_password) {
      toast.error('Tüm alanları doldurun'); return;
    }
    if (passwords.new_password.length < 6) {
      toast.error('Yeni şifre en az 6 karakter olmalı'); return;
    }
    if (passwords.new_password !== passwords.confirm_password) {
      toast.error('Yeni şifreler eşleşmiyor'); return;
    }
    setIsSubmittingPassword(true);
    try {
      await api.put('/auth/password', {
        current_password: passwords.current_password,
        new_password: passwords.new_password
      });
      toast.success('Şifre başarıyla güncellendi');
      setPasswords({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata oluştu');
    } finally {
      setIsSubmittingPassword(false);
    }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[hsl(var(--gold))] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="admin-profile">
      <h1 className="font-heading text-2xl font-semibold mb-6">Profilim</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Profile Info */}
        <div className="p-6 rounded-2xl bg-card border border-border shadow-[var(--shadow-elev-1)]">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-[hsl(var(--gold)/0.1)] flex items-center justify-center">
              <User className="w-5 h-5 text-[hsl(var(--gold))]" />
            </div>
            <h3 className="font-heading text-lg font-semibold">Kişisel Bilgiler</h3>
          </div>
          <form onSubmit={handleProfileSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1.5">Ad Soyad</label>
              <input
                value={profile.name}
                onChange={e => setProfile(p => ({ ...p, name: e.target.value }))}
                placeholder="Adınız Soyadınız"
                className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--gold))]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">E-posta <span className="text-destructive">*</span></label>
              <input
                type="email"
                value={profile.email}
                onChange={e => setProfile(p => ({ ...p, email: e.target.value }))}
                required
                className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--gold))]"
              />
            </div>
            <button
              type="submit"
              disabled={isSubmittingProfile}
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] text-sm font-medium hover:bg-[hsl(var(--gold)/0.92)] disabled:opacity-50"
              data-testid="admin-save-button"
            >
              <Save className="w-4 h-4" />
              {isSubmittingProfile ? 'Kaydediliyor...' : 'Kaydet'}
            </button>
          </form>
        </div>

        {/* Password Change */}
        <div className="p-6 rounded-2xl bg-card border border-border shadow-[var(--shadow-elev-1)]">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-[hsl(var(--gold)/0.1)] flex items-center justify-center">
              <Lock className="w-5 h-5 text-[hsl(var(--gold))]" />
            </div>
            <h3 className="font-heading text-lg font-semibold">Şifre Değiştir</h3>
          </div>
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1.5">Mevcut Şifre <span className="text-destructive">*</span></label>
              <input
                type="password"
                value={passwords.current_password}
                onChange={e => setPasswords(p => ({ ...p, current_password: e.target.value }))}
                className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--gold))]"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Yeni Şifre <span className="text-destructive">*</span></label>
              <input
                type="password"
                value={passwords.new_password}
                onChange={e => setPasswords(p => ({ ...p, new_password: e.target.value }))}
                className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--gold))]"
              />
              <p className="text-xs text-muted-foreground mt-1">En az 6 karakter</p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Yeni Şifre (Tekrar) <span className="text-destructive">*</span></label>
              <input
                type="password"
                value={passwords.confirm_password}
                onChange={e => setPasswords(p => ({ ...p, confirm_password: e.target.value }))}
                className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--gold))]"
              />
            </div>
            <button
              type="submit"
              disabled={isSubmittingPassword}
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] text-sm font-medium hover:bg-[hsl(var(--gold)/0.92)] disabled:opacity-50"
            >
              <Lock className="w-4 h-4" />
              {isSubmittingPassword ? 'Güncelleniyor...' : 'Şifreyi Güncelle'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
